/**
 * Domains.tsx — customer domain management, Glass + Aurora language.
 *
 * Sections:
 *   1. Expiry alert band (expired / expiring ≤30 days)
 *   2. Domain list — status, expiry, auto-renew + privacy toggles,
 *      expandable nameserver editor per domain
 *
 * Registrar side effects: auto-renew, privacy and nameserver changes call
 * AWS Route 53 Domains through the backend; operations can take a moment
 * to reflect at the registry, so each row shows a per-action busy state.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashShell from '../../components/dash/DashShell';
import GlassCard from '../../components/dash/GlassCard';
import Pill, { pillForStatus } from '../../components/dash/Pill';
import { domainService, Domain } from '../../services/domainService';

const DAY_MS = 24 * 60 * 60 * 1000;

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / DAY_MS);
}

function fmt(dateStr?: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const HOSTNAME_RE = /^(?=.{1,253}$)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const Domains: React.FC = () => {
  const { user } = useAuth();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [nsOpen, setNsOpen] = useState<string | null>(null);
  const [nsDraft, setNsDraft] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    const res = await domainService.getMyDomains();
    if (res.success) setDomains(res.data || []);
    else setErr(res.error || 'Failed to load domains');
    setLoading(false);
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const withBusy = async (key: string, fn: () => Promise<{ success: boolean; error?: string }>, successMsg: string): Promise<boolean> => {
    setBusy(key); setErr(''); setNotice('');
    try {
      const res = await fn();
      if (!res.success) { setErr(res.error || 'Operation failed'); return false; }
      setNotice(successMsg);
      await load();
      return true;
    } catch (e: any) {
      setErr(e.message || 'Network error');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const toggleAutoRenew = (d: Domain) =>
    withBusy(`renew-${d.id}`, () => domainService.toggleAutoRenew(d.id),
      `Auto-renew ${d.auto_renew ? 'disabled' : 'enabled'} for ${d.domain_name}. Registrar changes can take a few minutes.`);

  const togglePrivacy = (d: Domain) =>
    withBusy(`privacy-${d.id}`, () => domainService.togglePrivacyProtection(d.id),
      `WHOIS privacy ${d.privacy_protection ? 'disabled' : 'enabled'} for ${d.domain_name}.`);

  const openNsEditor = (d: Domain) => {
    setNsOpen(nsOpen === d.id ? null : d.id);
    setNsDraft((d.nameservers || []).join('\n'));
    setErr(''); setNotice('');
  };

  const saveNameservers = (d: Domain) => {
    const list = nsDraft.split('\n').map(s => s.trim().replace(/\.$/, '').toLowerCase()).filter(Boolean);
    if (list.length < 2) { setErr('At least two nameservers are required.'); return; }
    const bad = list.find(ns => !HOSTNAME_RE.test(ns));
    if (bad) { setErr(`"${bad}" is not a valid nameserver hostname.`); return; }
    if (!window.confirm(
      `Update nameservers for ${d.domain_name}?\n\n${list.join('\n')}\n\nThis changes where the domain's DNS is served from. ` +
      `If these point away from 12and22, hosting/email on this domain may stop resolving.`
    )) return;
    withBusy(`ns-${d.id}`, () => domainService.updateNameservers(d.id, list),
      `Nameservers updated for ${d.domain_name}. Propagation can take up to 48 hours.`)
      .then(ok => { if (ok) setNsOpen(null); });
  };

  const expiring = domains.filter(d => d.status !== 'expired' && daysUntil(d.expiration_date) <= 30);
  const expired = domains.filter(d => d.status === 'expired' || daysUntil(d.expiration_date) < 0);

  return (
    <DashShell
      title="Domains"
      subtitle="Manage registration, renewal, privacy and nameservers for your domains."
      right={
        <div className="flex items-center gap-3">
          <div className="glass-pill text-slate-700">
            <span className="text-slate-500">domains</span>
            <strong className="text-slate-900">{domains.length}</strong>
          </div>
          <Link
            to="/domains"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            Register new
          </Link>
        </div>
      }
    >
      {err && (
        <GlassCard delay={40} tint="rose" className="px-4 py-3 text-sm text-rose-700 mb-6">{err}</GlassCard>
      )}
      {notice && (
        <GlassCard delay={40} tint="emerald" className="px-4 py-3 text-sm text-emerald-700 mb-6">{notice}</GlassCard>
      )}

      {(expired.length > 0 || expiring.length > 0) && (
        <GlassCard delay={80} tint="amber" className="px-4 py-3 mb-8">
          <div className="text-sm text-amber-800">
            {expired.length > 0 && (
              <div className="font-medium">
                {expired.length} domain{expired.length > 1 ? 's have' : ' has'} expired: {expired.map(d => d.domain_name).join(', ')}. Contact support to recover.
              </div>
            )}
            {expiring.length > 0 && (
              <div>
                {expiring.length} domain{expiring.length > 1 ? 's' : ''} expiring within 30 days: {expiring.map(d => `${d.domain_name} (${daysUntil(d.expiration_date)}d)`).join(', ')}.
                {' '}Domains with auto-renew on will renew automatically.
              </div>
            )}
          </div>
        </GlassCard>
      )}

      <section>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 px-1">Your domains</h2>
        <GlassCard delay={120} className="p-2">
          {loading && domains.length === 0 ? (
            <div className="text-sm text-slate-500 p-4">Loading…</div>
          ) : domains.length === 0 ? (
            <div className="p-10 text-center">
              <div className="text-sm text-slate-500 mb-4">You don't own any domains yet.</div>
              <Link to="/domains" className="inline-flex items-center px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition">
                Search for a domain
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-slate-200/60">
              {domains.map(d => {
                const days = daysUntil(d.expiration_date);
                const statusPill = pillForStatus(d.status);
                return (
                  <li key={d.id} className="p-4 rounded-xl hover:bg-slate-50/50 transition">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Name + status + expiry */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-medium text-slate-900 break-all">{d.domain_name}</span>
                          <Pill label={statusPill.label} tone={statusPill.tone} />
                          {days <= 30 && days >= 0 && d.status !== 'expired' && (
                            <Pill label={`${days}d left`} tone="amber" />
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          Expires {fmt(d.expiration_date)} · Registered {fmt(d.registration_date)}
                          {d.registrar ? ` · via ${d.registrar}` : ''}
                        </div>
                      </div>

                      {/* Toggles */}
                      <div className="flex items-center gap-6">
                        <Toggle
                          label="Auto-renew"
                          on={d.auto_renew}
                          busy={busy === `renew-${d.id}`}
                          onClick={() => toggleAutoRenew(d)}
                        />
                        <Toggle
                          label="WHOIS privacy"
                          on={d.privacy_protection}
                          busy={busy === `privacy-${d.id}`}
                          onClick={() => togglePrivacy(d)}
                        />
                        <button
                          onClick={() => openNsEditor(d)}
                          className={`text-sm font-medium transition ${
                            nsOpen === d.id ? 'text-orange-700' : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Nameservers
                          <svg className={`inline w-3.5 h-3.5 ml-1 transition-transform ${nsOpen === d.id ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
                        </button>
                      </div>
                    </div>

                    {/* Nameserver editor */}
                    {nsOpen === d.id && (
                      <div className="mt-4 pt-4 border-t border-slate-200/60">
                        <div className="text-xs text-slate-500 mb-2">
                          One nameserver per line (2–6 entries). Current DNS is served by:{' '}
                          <span className="font-mono">{(d.nameservers || []).join(', ') || 'unknown'}</span>
                        </div>
                        <textarea
                          value={nsDraft}
                          onChange={e => setNsDraft(e.target.value)}
                          rows={4}
                          spellCheck={false}
                          className="w-full max-w-lg font-mono text-sm rounded-xl border border-slate-200 bg-white/70 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
                          placeholder={'ns1.example.com\nns2.example.com'}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <button
                            onClick={() => saveNameservers(d)}
                            disabled={busy === `ns-${d.id}`}
                            className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition"
                          >
                            {busy === `ns-${d.id}` ? 'Saving…' : 'Save nameservers'}
                          </button>
                          <button
                            onClick={() => setNsOpen(null)}
                            className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:text-slate-900 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </GlassCard>
      </section>
    </DashShell>
  );
};

/** Small labeled switch, per-row busy-aware. */
const Toggle: React.FC<{ label: string; on: boolean; busy?: boolean; onClick: () => void }> = ({ label, on, busy, onClick }) => (
  <button
    onClick={onClick}
    disabled={busy}
    className="flex items-center gap-2 group disabled:opacity-60"
    aria-pressed={on}
    aria-label={`${label}: ${on ? 'on' : 'off'}`}
  >
    <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-1'} ${busy ? 'animate-pulse' : ''}`} />
    </span>
    <span className="text-xs text-slate-600 group-hover:text-slate-900 transition whitespace-nowrap">{label}</span>
  </button>
);

export default Domains;
